import { useEffect, useRef } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { drumProducts } from '@/mocks/products';

function RevealDiv({ children, className, style }: { children: React.ReactNode; className?: string; style?: React.CSSProperties }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { el.classList.add('ew-revealed'); obs.unobserve(el); } }, { threshold: 0.1 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return <div ref={ref} className={`ew-reveal ${className ?? ''}`} style={style}>{children}</div>;
}

const drums = drumProducts;

export default function MetalDrums() {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 pt-20">
        {/* Hero */}
        <section className="relative py-28 flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img src="https://public.readdy.ai/ai/img_res/38e3c10f7acd6f8b5de82e850d3292a3.jpg" alt="Metal Drums hero" className="w-full h-full object-cover object-top" />
            <div className="absolute inset-0 bg-gradient-to-b from-black/65 via-black/40 to-black/65"></div>
          </div>
          <div className="relative z-10 text-center px-4 max-w-3xl mx-auto">
            <p style={{ fontFamily: '"Dancing Script", cursive', color: '#E8B96A', fontSize: '1.3rem' }} className="mb-2">Where music meets meditation</p>
            <h1 style={{ fontFamily: '"Playfair Display", serif', color: '#fff' }} className="text-5xl md:text-6xl font-black mb-4">Metal Drums</h1>
            <p style={{ fontFamily: '"Lato", sans-serif', color: '#EDE5D8', lineHeight: 1.8 }} className="text-lg mb-8">
              Handcrafted steel tongue drums with rich, resonant tones perfect for yoga, meditation, relaxation, and musical exploration. Rooted in Earth Wisdom&apos;s 40-year heritage of healing music.
            </p>
            <a href="#drum-grid" className="inline-flex items-center gap-2 px-8 py-4 rounded-lg font-bold uppercase tracking-wide whitespace-nowrap transition-all duration-200 hover:-translate-y-0.5" style={{ backgroundColor: '#C9933A', color: '#fff', fontFamily: '"Lato", sans-serif' }}>
              Explore Drums
            </a>
          </div>
        </section>

        {/* Heritage section */}
        <section style={{ backgroundColor: '#1E4F4F' }} className="py-16">
          <div className="max-w-5xl mx-auto px-4 md:px-8 flex flex-col lg:flex-row items-center gap-12">
            <RevealDiv className="flex-1">
              <p style={{ fontFamily: '"Dancing Script", cursive', color: '#E8B96A', fontSize: '1.2rem' }} className="mb-2">Our Musical Roots</p>
              <h2 style={{ fontFamily: '"Playfair Display", serif', color: '#FAF7F2' }} className="text-3xl font-bold mb-4">Born from a Love of Healing Sound</h2>
              <p style={{ fontFamily: '"Lato", sans-serif', color: '#A0C8C8', lineHeight: 1.8 }} className="text-base mb-4">
                Earth Wisdom™ started in 1981 as one of the first retailers specializing in music for yoga, massage, and relaxation. That deep connection to healing sound is woven into everything we do — including our metal drum collection.
              </p>
              <p style={{ fontFamily: '"Lato", sans-serif', color: '#A0C8C8', lineHeight: 1.8 }} className="text-base">
                Our steel tongue drums produce hauntingly beautiful, meditative tones that are easy to play — no musical experience required. Whether you&apos;re a seasoned musician or a curious beginner, these drums invite you to slow down, breathe, and create.
              </p>
            </RevealDiv>
            <RevealDiv className="flex-1 w-full">
              <div className="rounded-2xl overflow-hidden" style={{ height: '320px' }}>
                <img src="https://readdy.ai/api/search-image?query=peaceful%20yoga%20meditation%20studio%20with%20steel%20tongue%20drum%20candles%20crystals%20zen%20atmosphere%20warm%20golden%20light%20healing%20music%20instruments%20spiritual%20practice&width=700&height=320&seq=drum-heritage-001&orientation=landscape" alt="Healing music heritage" className="w-full h-full object-cover object-top" />
              </div>
            </RevealDiv>
          </div>
        </section>

        {/* Features */}
        <section className="py-16" style={{ backgroundColor: '#F5F0E8' }}>
          <div className="max-w-5xl mx-auto px-4 md:px-8">
            <RevealDiv className="text-center mb-10">
              <h2 style={{ fontFamily: '"Playfair Display", serif', color: '#4A3728' }} className="text-3xl font-bold">What Makes These Drums Special</h2>
            </RevealDiv>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: 'ri-music-2-line', title: 'Rich Resonance', desc: 'Deep, sustained tones that fill any space with warmth and calm.' },
                { icon: 'ri-user-smile-line', title: 'Easy to Play', desc: 'No musical training needed — anyone can create beautiful music.' },
                { icon: 'ri-heart-pulse-line', title: 'Healing Tones', desc: 'Perfect for yoga, meditation, sound baths, and relaxation.' },
                { icon: 'ri-tools-line', title: 'Handcrafted', desc: 'Each drum is crafted with care for lasting quality and beauty.' },
              ].map((f, i) => (
                <RevealDiv key={f.title} style={{ transitionDelay: `${i * 0.08}s` }}>
                  <div className="p-6 rounded-2xl text-center h-full" style={{ backgroundColor: '#fff' }}>
                    <span className="w-12 h-12 flex items-center justify-center mx-auto mb-3 rounded-full" style={{ backgroundColor: '#EDE5D8' }}>
                      <i className={`${f.icon} text-xl`} style={{ color: '#C9933A' }}></i>
                    </span>
                    <h3 style={{ fontFamily: '"Playfair Display", serif', color: '#4A3728' }} className="text-base font-bold mb-2">{f.title}</h3>
                    <p style={{ fontFamily: '"Lato", sans-serif', color: '#7A5C42', lineHeight: 1.7 }} className="text-sm">{f.desc}</p>
                  </div>
                </RevealDiv>
              ))}
            </div>
          </div>
        </section>

        {/* Drum Grid */}
        <section id="drum-grid" className="py-20" style={{ backgroundColor: '#FAF7F2' }}>
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <RevealDiv className="text-center mb-12">
              <h2 style={{ fontFamily: '"Playfair Display", serif', color: '#4A3728' }} className="text-4xl font-bold">Our Drum Collection</h2>
              <p style={{ fontFamily: '"Lato", sans-serif', color: '#7A5C42' }} className="text-base mt-3">Call or email to check availability and pricing.</p>
            </RevealDiv>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-product-shop>
              {drums.map((d, i) => (
                <RevealDiv key={d.id} style={{ transitionDelay: `${i * 0.08}s` }}>
                  <div className="rounded-2xl overflow-hidden group cursor-pointer" style={{ backgroundColor: '#fff' }}>
                    <div className="overflow-hidden" style={{ height: '260px' }}>
                      <img src={d.img} alt={d.title} className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
                    </div>
                    <div className="p-5">
                      <div className="flex gap-2 mb-2 flex-wrap">
                        <span className="text-xs font-bold px-2 py-1 rounded-full" style={{ backgroundColor: '#EDE5D8', color: '#C9933A', fontFamily: '"Lato", sans-serif' }}>{d.notes}</span>
                        <span className="text-xs font-bold px-2 py-1 rounded-full" style={{ backgroundColor: '#1E4F4F', color: '#E8B96A', fontFamily: '"Lato", sans-serif' }}>{d.key}</span>
                      </div>
                      <h3 style={{ fontFamily: '"Playfair Display", serif', color: '#4A3728' }} className="text-base font-bold mb-1">{d.title}</h3>
                      <p style={{ fontFamily: '"Lato", sans-serif', color: '#C9933A', fontWeight: 700 }} className="text-sm mb-3">{d.price}</p>
                      <a href="tel:7346468620" className="inline-flex items-center gap-1.5 text-sm font-bold whitespace-nowrap" style={{ color: '#C9933A', fontFamily: '"Lato", sans-serif' }}>
                        Call to Inquire <i className="ri-arrow-right-line"></i>
                      </a>
                    </div>
                  </div>
                </RevealDiv>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <RevealDiv>
          <section className="py-16" style={{ background: 'linear-gradient(135deg, #1E4F4F 0%, #4A3728 100%)' }}>
            <div className="max-w-3xl mx-auto px-4 text-center">
              <h2 style={{ fontFamily: '"Playfair Display", serif', color: '#FAF7F2' }} className="text-3xl font-bold mb-4">Ready to Find Your Rhythm?</h2>
              <p style={{ fontFamily: '"Lato", sans-serif', color: '#A0C8C8', lineHeight: 1.8 }} className="text-base mb-8">
                Contact us to learn more about our metal drum selection, pricing, and availability.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="tel:7346468620" className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-lg font-bold uppercase tracking-wide whitespace-nowrap transition-all duration-200 hover:-translate-y-0.5" style={{ backgroundColor: '#C9933A', color: '#fff', fontFamily: '"Lato", sans-serif' }}>
                  <i className="ri-phone-line"></i> Call Us
                </a>
                <a href="mailto:earthwisdom80@gmail.com" className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-lg font-bold uppercase tracking-wide whitespace-nowrap border-2 border-[#E8B96A] transition-all duration-200 hover:-translate-y-0.5" style={{ color: '#E8B96A', fontFamily: '"Lato", sans-serif', backgroundColor: 'transparent' }}>
                  <i className="ri-mail-line"></i> Email Us
                </a>
              </div>
            </div>
          </section>
        </RevealDiv>
      </main>
      <Footer />
    </div>
  );
}
